import { TrendingUp, TrendingDown, ChevronRight, AlertTriangle, Heart } from "lucide-react";
import type { Influencer } from "@/types/influencer";
import type { LeaderboardEntry } from "@/hooks/useLeaderboard";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import AwardBadge from "@/components/AwardBadge";

interface FollowState {
  isFollowing: boolean;
  onToggle: () => void;
}

interface InfluencerCardProps {
  influencer: Influencer | LeaderboardEntry;
  rank: number;
  onClick: () => void;
  followState?: FollowState;
}

const MIN_CALLS = 10;

const InfluencerCard = ({ influencer, rank, onClick, followState }: InfluencerCardProps) => {
  const lowCalls = influencer.totalCalls < MIN_CALLS;
  const netPnl = 'netPnlCaptured' in influencer && influencer.netPnlCaptured != null ? influencer.netPnlCaptured : influencer.totalCalls * influencer.avgPnl;
  const score = 'score' in influencer && influencer.score != null ? influencer.score : Math.log10(Math.max(influencer.totalCalls, 1)) * influencer.avgPnl;
  const totalActionableCalls = 'totalActionableCalls' in influencer && influencer.totalActionableCalls != null ? influencer.totalActionableCalls : influencer.totalCalls;

  const handleFollowClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    followState?.onToggle();
  };

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-card hover:glow-border hover:relative hover:z-10 transition-all duration-300 group cursor-pointer border-b border-border last:border-b-0"
    >
      {/* Desktop layout */}
      <div className="hidden md:grid grid-cols-[60px_minmax(160px,1fr)_100px_100px_100px_100px_110px_140px_40px] items-center gap-4 px-5 py-4">
        {/* Rank */}
        <div className="font-mono font-bold text-primary text-sm flex items-center justify-between">
          #{rank}
          <AwardBadge handle={influencer.handle} />
        </div>

        {/* Influencer */}
        <div className="flex items-center gap-3 min-w-0">
          {followState && (
            <button
              onClick={handleFollowClick}
              className={`shrink-0 p-1 rounded-md transition-colors ${
                followState.isFollowing
                  ? "text-primary hover:text-primary/70"
                  : "text-muted-foreground/40 hover:text-primary/60"
              }`}
              aria-label={followState.isFollowing ? "Unfollow" : "Follow"}
            >
              <Heart className={`w-4 h-4 ${followState.isFollowing ? "fill-primary" : ""}`} />
            </button>
          )}
          <img
            src={influencer.avatar}
            alt={influencer.displayName}
            className="w-10 h-10 rounded-full bg-secondary shrink-0"
          />
          <div className="min-w-0">
            <span className="font-semibold text-foreground truncate flex items-center gap-1.5">
              {influencer.displayName}
            </span>
            <span className="font-mono text-xs text-muted-foreground">
              @{influencer.handle.replace(/^@/, "")}
            </span>
          </div>
        </div>

        {/* Score */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={`font-mono font-bold text-sm text-center cursor-default ${score > 0 ? "text-profit" : score < 0 ? "text-loss" : "text-muted-foreground"}`}>
              {score > 0 ? "+" : ""}{score.toFixed(1)}
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p className="text-xs font-semibold mb-1">Score</p>
            <p className="text-xs text-muted-foreground">= Log₁₀(Total Positions) × Avg PnL</p>
            <p className="text-xs text-muted-foreground">= {Math.log10(Math.max(influencer.totalCalls, 1)).toFixed(2)} × {influencer.avgPnl > 0 ? "+" : ""}{influencer.avgPnl.toFixed(1)}% = {score > 0 ? "+" : ""}{score.toFixed(1)}</p>
          </TooltipContent>
        </Tooltip>

        {/* Avg PnL */}
        <div className={`font-mono font-bold text-sm flex items-center justify-center gap-1 ${influencer.avgPnl > 0 ? "text-profit" : influencer.avgPnl < 0 ? "text-loss" : "text-muted-foreground"}`}>
          {influencer.avgPnl > 0 ? <TrendingUp className="w-3 h-3" /> : influencer.avgPnl < 0 ? <TrendingDown className="w-3 h-3" /> : null}
          {influencer.avgPnl > 0 ? "+" : ""}{influencer.avgPnl.toFixed(1)}%
        </div>

        {/* PnL/day */}
        <div className="font-mono font-bold text-sm text-center text-foreground">
          {influencer.avgPnlPerDay > 0 ? "+" : ""}{influencer.avgPnlPerDay.toFixed(2)}%
        </div>

        {/* Win Rate */}
        <div className={`font-mono font-bold text-sm text-center ${influencer.winRate > 0 ? (influencer.winRate >= 50 ? "text-profit" : "text-loss") : "text-muted-foreground"}`}>
          {influencer.winRate}%
        </div>

        {/* Net PnL Captured */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={`font-mono font-bold text-sm text-center cursor-default ${netPnl > 0 ? "text-profit" : netPnl < 0 ? "text-loss" : "text-muted-foreground"}`}>
              {netPnl > 0 ? "+" : ""}{netPnl.toFixed(1)}%
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p className="text-xs font-semibold mb-1">Net PnL Captured</p>
            <p className="text-xs text-muted-foreground">= Total Positions × Avg PnL</p>
            <p className="text-xs text-muted-foreground">= {influencer.totalCalls} × {influencer.avgPnl > 0 ? "+" : ""}{influencer.avgPnl.toFixed(1)}% = {netPnl > 0 ? "+" : ""}{netPnl.toFixed(1)}%</p>
          </TooltipContent>
        </Tooltip>

        {/* Calls / Positions */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="font-mono text-sm text-foreground text-center cursor-default flex items-center justify-center gap-1">
              {totalActionableCalls} / {influencer.totalCalls}
              {lowCalls && <AlertTriangle className="w-3.5 h-3.5 text-yellow-500 shrink-0" />}
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p className="text-xs font-semibold mb-1">Calls / Positions</p>
            <p className="text-xs text-muted-foreground">Calls = actionable tweets (excl. WATCH, NOISE)</p>
            <p className="text-xs text-muted-foreground">Positions = grouped positions per asset</p>
            {lowCalls && <p className="text-xs text-yellow-500 mt-1">Fewer than {MIN_CALLS} positions — stats may not be reliable</p>}
          </TooltipContent>
        </Tooltip>

        {/* Arrow */}
        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
      </div>

      {/* Mobile layout */}
      <div className="md:hidden px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="font-mono font-bold text-primary text-xs w-7 shrink-0 text-center">
            #{rank}
          </div>
          {followState && (
            <button
              onClick={handleFollowClick}
              className={`shrink-0 p-1 rounded-md transition-colors ${
                followState.isFollowing
                  ? "text-primary hover:text-primary/70"
                  : "text-muted-foreground/40 hover:text-primary/60"
              }`}
              aria-label={followState.isFollowing ? "Unfollow" : "Follow"}
            >
              <Heart className={`w-3.5 h-3.5 ${followState.isFollowing ? "fill-primary" : ""}`} />
            </button>
          )}
          <img
            src={influencer.avatar}
            alt={influencer.displayName}
            className="w-8 h-8 rounded-full bg-secondary shrink-0"
          />
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="flex items-center justify-between gap-1">
              <span className="font-semibold text-foreground text-sm truncate">
                {influencer.displayName}
              </span>
              <AwardBadge handle={influencer.handle} />
            </div>
            <span className="font-mono text-[11px] text-muted-foreground block truncate">
              @{influencer.handle.replace(/^@/, "")}
            </span>
          </div>
          <div className={`font-mono font-bold text-sm shrink-0 ${influencer.avgPnl > 0 ? "text-profit" : influencer.avgPnl < 0 ? "text-loss" : "text-muted-foreground"}`}>
            {influencer.avgPnl > 0 ? "+" : ""}{influencer.avgPnl.toFixed(1)}%
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
        </div>
        <div className="flex items-center gap-3 mt-1.5 ml-[calc(1.75rem+0.5rem+2rem+0.5rem)] text-[11px] font-mono text-muted-foreground">
          <span className={score > 0 ? "text-profit" : score < 0 ? "text-loss" : ""}>
            Score: {score > 0 ? "+" : ""}{score.toFixed(1)}
          </span>
          <span className={influencer.winRate >= 50 ? "text-profit" : "text-loss"}>
            {influencer.winRate}% win
          </span>
          <span>{totalActionableCalls}/{influencer.totalCalls} calls</span>
        </div>
      </div>
    </button>
  );
};

export default InfluencerCard;
